import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { setDocumentsVerified } from "@/stores/documents/repository";
import { PERMISSIONS } from "@/constants/permissions";
import type { DocumentVerifiedTranslations } from "@/lib/i18n/types/documents-sections/verified";

/** Minimal shape needed to gate the action and read the current mark. */
export interface VerifiableDocument {
  id: string | number;
  project_id?: number | string | null;
  verified_at?: string | null;
}

/**
 * The row shape a verify write acts on. `verified_at` is REQUIRED here, unlike
 * on VerifiableDocument: it is what `setVerified`'s direction filter reads, and
 * a row that simply omits it reads as "not verified" — so an unverify over rows
 * typed without the field would filter every one of them out and answer a real
 * click with "nothing to update", with no type error to catch it. Callers
 * resolve the mark (`?? null`) instead.
 */
export type VerifiableDocumentMark = Pick<VerifiableDocument, "id"> & {
  verified_at: string | null;
};

/** Aggregate Verified state of a selection — decides the action's label. */
export type VerifiedSelectionState = "all" | "none" | "mixed";

export function verifiedSelectionState(docs: VerifiableDocument[]): VerifiedSelectionState {
  if (docs.length === 0) return "none";
  const verified = docs.filter((doc) => doc.verified_at != null).length;
  if (verified === 0) return "none";
  if (verified === docs.length) return "all";
  return "mixed";
}

/** What the user is told about a write, and whether it may be undone. */
export interface VerifyToastPlan {
  description: string;
  variant?: "destructive";
  /** Only a write that changed rows leaves anything to put back. */
  undoable: boolean;
}

/**
 * Turn the server's answer into the one thing the user sees.
 *
 * Pure, and separate from the write, because the four outcomes are easy to
 * confuse: RLS refused everything; it refused some; it refused nothing but the
 * rows were already in the requested state (a stale client, or someone else
 * got there first); and the write did what was asked. Only the first is a
 * failure with nothing to show for it — the partial one still changed rows,
 * so it keeps its Undo.
 *
 * `requested` is the WHOLE selection — every row the click named, including
 * the ones whose outcome went unread. Any smaller denominator is a number the
 * user never chose: subtracting the unreadable rows answered a four-row
 * selection with "only 2 of 3". What the unknown rows must not do is read as
 * refusals, so they are named in their own clause instead of silently swelling
 * the shortfall.
 *
 * `transitioned` and `alreadyInState` are reported separately whenever both
 * exist, because Undo can only put back the rows this write changed: "Marked
 * 3" over an Undo that reverts 1 is a count the affordance behind it
 * contradicts. Neither is dropped — counting only `transitioned` reports "1 of
 * 3" for a click that left all three verified, which reads as a partial
 * failure, and counting only the sum hides which rows Undo owns.
 *
 * Both are about the SELECTION, not about the write: `alreadyInState` covers
 * rows the caller never sent, reconciled by the server's read rather than
 * assumed settled from the cached mark that held them back.
 */
export function verifyToastPlan(
  copy: DocumentVerifiedTranslations,
  next: boolean,
  requested: number,
  transitioned: number,
  alreadyInState: number,
  unreconciled = 0
): VerifyToastPlan {
  // What the click asked for is now true of this many rows, whether this write
  // made it true or found it so.
  const settled = transitioned + alreadyInState;
  // Rows the click named that the server neither settled nor left unread: the
  // refusals, and the only thing that makes this a partial write.
  const refused = requested - settled - unreconciled;
  if (settled === 0) return { description: copy.error, variant: "destructive", undoable: false };
  if (refused > 0) {
    const template = unreconciled > 0 ? copy.partialErrorWithUnknown : copy.partialError;
    return {
      description: template
        .replace("{count}", String(settled))
        .replace("{total}", String(requested))
        .replace("{unknown}", String(unreconciled)),
      variant: "destructive",
      undoable: true,
    };
  }
  if (transitioned === 0) {
    // Nothing changed AND rows whose state nobody could read back: the only
    // rows that settled are ones the caller filtered out before writing, so
    // "already verified — nothing to update" would report an unknown outcome
    // as a reassuring success for a selection this write may well have been
    // refused on. A retryable failure is the honest answer — a retry re-sends
    // only the unknown rows, and transitions any the server did refuse.
    if (unreconciled > 0) {
      return { description: copy.error, variant: "destructive", undoable: false };
    }
    return {
      description: next ? copy.alreadyVerified : copy.alreadyUnverified,
      undoable: false,
    };
  }
  // Every row the click named is now in the state it asked for — but not all
  // by this write. Naming the two groups keeps the click off a partial-failure
  // reading ("2 of 3" for a batch whose third row someone else had verified)
  // without promising an Undo over rows that were never ours to change: the
  // count it reverts is the one this copy calls marked.
  if (alreadyInState > 0) {
    return {
      description: (next ? copy.verifiedToastWithAlready : copy.unverifiedToastWithAlready)
        .replace("{count}", String(transitioned))
        .replace("{already}", String(alreadyInState)),
      undoable: true,
    };
  }
  return {
    description: (next ? copy.verifiedToast : copy.unverifiedToast).replace(
      "{count}",
      String(transitioned)
    ),
    undoable: true,
  };
}

/**
 * Verify / unverify documents, from any of the views that list them.
 *
 * Centralised so List, Timeline and Folders behave identically: same
 * permission gate, same optimistic patch, same Undo toast. The write is a
 * single `documents` UPDATE — the DB trigger decides the timestamp and the
 * verifier, and `applyVerified` reflects that decision into every cached copy
 * of the row.
 */
export function useDocumentVerification() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const perms = usePermissions();
  const documentStore = useDocumentStore();
  const queryClient = useQueryClient();

  const canModifyData = perms.hasPermission(PERMISSIONS.MODIFY_DATA);
  const { canModifyFilesInProject } = perms;

  /**
   * Whether the current user may change this document's Verified mark.
   * Identical to the rename gate: org-wide MODIFY_DATA, or
   * MODIFY_PROJECT_FILES on the document's own project. RLS enforces the write;
   * this only hides an affordance that would be rejected anyway.
   */
  const canVerifyDocument = useCallback(
    (doc: VerifiableDocument) => {
      if (canModifyData) return true;
      const projectId = doc.project_id;
      if (projectId === null || projectId === undefined || projectId === "") return false;
      const numeric = typeof projectId === "string" ? parseInt(projectId, 10) : projectId;
      if (Number.isNaN(numeric)) return false;
      return canModifyFilesInProject(numeric);
    },
    [canModifyData, canModifyFilesInProject]
  );

  const setVerified = useCallback(
    async (documents: VerifiableDocumentMark[], verified: boolean): Promise<void> => {
      const copy = t.documents.verified;

      // `offerUndo` is false for the undo itself, so the toasts can't ping-pong.
      // `selection` is what the click named, which is wider than `ids` whenever
      // rows were dropped for being in the target state already; it defaults to
      // the ids because an undo names exactly the rows it puts back. The write
      // still only touches `ids`, but the whole selection is reconciled, so
      // every row the user is told about was read back from the server.
      const run = async (
        ids: string[],
        next: boolean,
        offerUndo = false,
        selection = ids
      ): Promise<void> => {
        try {
          const { transitioned, alreadyInState, unchanged, unreconciled } =
            await setDocumentsVerified(ids, next, selection);
          // Every one of these is the server's own truth — the first because
          // this write set it, the other two because we read them back — so
          // applying all three is what corrects the stale row that sent an
          // already-verified document here in the first place. `unchanged`
          // belongs in the same patch even though it settles nothing: those
          // rows are precisely the ones the client had wrong, and leaving them
          // uncorrected means the next click filters on the same stale mark.
          documentStore.applyVerified([...transitioned, ...alreadyInState, ...unchanged]);
          void queryClient.invalidateQueries({ queryKey: ["signing"] });

          // The undo set is the SERVER's transitioned rows, never the ids we
          // asked about: only these are ours to put back. A row someone else
          // had already verified is not among them, and clearing its
          // attribution is state the DB cannot restore. Built before the plan
          // is read — a partial write still changed real rows, and dropping the
          // affordance leaves them with no way back.
          const undoIds = transitioned.map((row) => String(row.id));
          // Undo is only offered for the verify direction: undoing an
          // unverify would re-stamp now() + the undoing user, silently
          // rewriting the original attribution (the trigger stamps every
          // unverified→verified transition).
          const undoable = next && offerUndo && undoIds.length > 0;

          // The denominator is the whole selection, because that is the only
          // number the user picked. Rows whose outcome nobody could read back
          // (the reconcile read failed) are neither settled nor refused, so
          // `unreconciled` rides along and the plan speaks for them in their
          // own clause: calling them refused turns a write that did everything
          // asked of it into a partial failure, and calling them settled claims
          // a state we never read. With nothing transitioned there is no
          // evidence to reassure anyone with, and the plan says so instead of
          // reporting the click as settled.
          const plan = verifyToastPlan(
            copy,
            next,
            selection.length,
            transitioned.length,
            alreadyInState.length,
            unreconciled
          );
          toast({
            description: plan.description,
            variant: plan.variant,
            action:
              undoable && plan.undoable ? (
                <ToastAction altText={copy.undo} onClick={() => void run(undoIds, false)}>
                  {copy.undo}
                </ToastAction>
              ) : undefined,
          });
        } catch (error) {
          console.error("Failed to update document verification:", error);
          toast({ variant: "destructive", description: copy.error });
        }
      };

      // Nothing selected: no write, and no toast either — "already verified"
      // would answer a question nobody asked.
      if (documents.length === 0) return;

      // Only the rows this action actually transitions. A bulk action offers
      // "Mark as verified" on a mixed selection, and the trigger freezes a row
      // that is already in the target state — so sending those achieves
      // nothing on the server while asking for a permission the action never
      // required, and in the unverify direction it is worse than nothing: a
      // row the client shows unverified but the server has verified would be
      // cleared, wiping an attribution the user never saw.
      const ids = documents
        .filter((doc) => (doc.verified_at != null) !== verified)
        .map((doc) => String(doc.id));
      // Never the whole selection: unverifying a row that was verified
      // beforehand would wipe an attribution the user never touched. `run`
      // narrows further still, to the rows the server reports it transitioned.
      // The whole selection still goes along to be RECONCILED, though — the
      // rows held back were held back on the strength of a cached mark, and
      // that cache is exactly what a filtered-out row calls into question.
      // Answering from it ("already verified — nothing to update") would
      // confirm a wrong badge to the user and leave it wrong; the read-back
      // either corrects it or reports the rows the click did not settle. With
      // every row filtered out there is nothing to write at all, and the call
      // becomes that read alone.
      await run(
        ids,
        verified,
        verified,
        documents.map((doc) => String(doc.id))
      );
    },
    [documentStore, queryClient, t, toast]
  );

  return { canVerifyDocument, setVerified };
}
